/**
 * Rapprochement d'un compte publicitaire avec les identifiants stockés sur un site.
 *
 * Un même compte porte plusieurs identités selon le chemin par lequel il a été rattaché :
 *
 * - `Site.accountIds` contient tantôt l'identifiant de base de l'élément de bibliothèque
 *   (un cuid), tantôt l'identifiant de la régie, écrit avec ou sans tirets
 *   (`123-136-3717` ou `1231363717`) ;
 * - `/api/library` expose `id` **égal au sourceId**, et range l'identifiant de base sous
 *   `libraryId` et `databaseId`.
 *
 * Comparer, comme le faisait le code, `account.id` et `account.sourceId` aux valeurs
 * stockées revenait donc à ne tester qu'une seule de ces identités : un site enregistré
 * avec des cuid ne reconnaissait aucun de ses comptes, et le dashboard affichait
 * « Aucun compte connecté » alors que la page d'accueil montrait ses chiffres.
 *
 * On compare ici toutes les identités connues, tirets normalisés des deux côtés.
 */

/** Un identifiant de régie s'écrit avec ou sans tirets selon l'époque de l'import. */
const sansTirets = (valeur) => String(valeur).replace(/-/g, '');

/** Toutes les écritures sous lesquelles un compte peut être désigné. */
export const identitesDuCompte = (compte) => {
  if (!compte) return [];

  const brutes = [compte.id, compte.sourceId, compte.libraryId, compte.databaseId]
    .filter(valeur => valeur !== undefined && valeur !== null && valeur !== '');

  return [...new Set(brutes.flatMap(valeur => [String(valeur), sansTirets(valeur)]))];
};

/** Ensemble d'identifiants autorisés, chaque valeur indexée avec et sans tirets. */
export const ensembleAutorise = (identifiants = []) => {
  const ensemble = new Set();
  identifiants.filter(Boolean).forEach(identifiant => {
    ensemble.add(String(identifiant));
    ensemble.add(sansTirets(identifiant));
  });
  return ensemble;
};

/** Vrai si le compte est désigné par l'une des identités autorisées. */
export const compteAutorise = (compte, autorises) => {
  if (!autorises || autorises.size === 0) return false;
  return identitesDuCompte(compte).some(identite => autorises.has(identite));
};
